import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import SettingNav from './SettingNav';
import "../../asset/css/profile-info.css";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';

const ProfileInfo = () => {
    // Loading State
    const [isLoading, setIsLoading] = useState(true);

    // State for Profile Data
    const [profile, setProfile] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        company_name: ""
    });

    // State for Password Fields
    const [passwords, setPasswords] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    // State for Password Visibility
    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const userToken = localStorage.getItem('userToken');

    // SweetAlert Toast Configuration
    const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    // 1. Fetch Profile Data on Load
    useEffect(() => {
        const fetchProfile = async () => {
            setIsLoading(true);
            try {
                const res = await axios.get(`${baseurl}getUserProfile`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                if (res.data.success) {
                    setProfile(res.data.data);
                }
            } catch (err) {
                console.error("Fetch Error:", err);
                Toast.fire({ icon: 'error', title: 'Failed to load profile' });
            } finally {
                // Small delay to ensure smooth transition
                setTimeout(() => setIsLoading(false), 600);
            }
        };
        fetchProfile();
    }, [userToken]);

    // Handle Input Changes for Profile
    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    // Handle Input Changes for Password
    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    // Toggle Password Visibility
    const toggleVisibility = (field) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    };

    // 2. Update Profile Method
    const updateProfile = async () => {
        try {
            const res = await axios.put(`${baseurl}updateUserProfile`, profile, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            if (res.data.success) {
                Toast.fire({ icon: 'success', title: 'Profile updated successfully' });
            }
        } catch (err) {
            Toast.fire({ icon: 'error', title: err.response?.data?.message || 'Update failed' });
        }
    };

    // 3. Update Password Method
    const updatePassword = async () => {
        if (passwords.newPassword !== passwords.confirmPassword) {
            return Toast.fire({ icon: 'error', title: 'Passwords do not match' });
        }

        try {
            const res = await axios.put(`${baseurl}updatePassword`, {
                currentPassword: passwords.currentPassword,
                newPassword: passwords.newPassword
            }, {
                headers: { Authorization: `Bearer ${userToken}` }
            });

            if (res.data.success) {
                Toast.fire({ icon: 'success', title: 'Password changed successfully' });
                setPasswords({ currentPassword: "", newPassword: "", confirmPassword: "" });
            }
        } catch (err) {
            Toast.fire({ icon: 'error', title: err.response?.data?.message || 'Password update failed' });
        }
    };

    const handlePhoneChange = (e) => {
    const { name, value } = e.target;

    // 1. Remove any character that is NOT a digit
    // 2. Limit the string length to 10
    const sanitizedValue = value.replace(/\D/g, '').slice(0, 10);

    // Update your profile state
    setProfile(prev => ({
        ...prev,
        [name]: sanitizedValue
    }));
};


    // Skeleton Component for the Profile Form
    const SkeletonForm = () => (
        <div className="skeleton-wrapper">
            <div className="skeleton-header mb-4 d-flex gap-3">
                <div className="skeleton-circle" style={{ width: '50px', height: '50px', borderRadius: '50%', background: '#e0e0e0' }}></div>
                <div className="flex-grow-1">
                    <div className="skeleton-line mb-2" style={{ width: '40%', height: '20px', background: '#e0e0e0' }}></div>
                    <div className="skeleton-line" style={{ width: '60%', height: '12px', background: '#e0e0e0' }}></div>
                </div>
            </div>
            {[1, 2, 3, 4].map((i) => (
                <div key={i} className="mb-3">
                    <div className="skeleton-line mb-2" style={{ width: '30%', height: '12px', background: '#e0e0e0' }}></div>
                    <div className="skeleton-line" style={{ width: '100%', height: '45px', borderRadius: '8px', background: '#e0e0e0' }}></div>
                </div>
            ))}
            <div className="d-flex gap-2 mt-4">
                <div className="skeleton-line" style={{ width: '120px', height: '40px', borderRadius: '8px', background: '#e0e0e0' }}></div>
                <div className="skeleton-line" style={{ width: '100px', height: '40px', borderRadius: '8px', background: '#e0e0e0' }}></div>
            </div>
        </div>
    );

    return (
        <div className="app-container">
            <Topbar />
            <div className="main-wrapper" style={{ background: '#F4F7FE' }}>
                <SideMenu />
                <div className="profile-content-container p-3" style={{ flex: 1, overflow: 'auto' }}>
                    <SettingNav />

                    <div className="row g-4 mt-0">
                        {/* LEFT: Profile Form */}
                        <div className="col-lg-6">
                            <div className="premium-card">
                                {isLoading ? (
                                    <SkeletonForm />
                                ) : (
                                    <>
                                        <div className="section-header">
                                            <div className="icon-wrap profile-icon">
                                                <i className="fa-solid fa-user-circle"></i>
                                            </div>
                                            <div>
                                                <h5 className="card-title">Profile Information</h5>
                                                <p className="card-subtitle">Update your personal details</p>
                                            </div>
                                        </div>

                                        <div className="form-body">
                                            <div className="row">
                                                <div className="col-md-6 ">
                                                    <label className="input-label">First Name</label>
                                                    <input name="first_name" type="text" className="form-control-premium" value={profile.first_name || ""} onChange={handleProfileChange} />
                                                </div>
                                                <div className="col-md-6 ">
                                                    <label className="input-label">Last Name</label>
                                                    <input name="last_name" type="text" className="form-control-premium" value={profile.last_name || ""} onChange={handleProfileChange} />
                                                </div>
                                            </div>
                                            <div className="input-block">
                                                <label className="input-label">Email Address</label>
                                                <input name="email" type="email" className="form-control-premium" value={profile.email || ""} disabled style={{ backgroundColor: '#e9ecef' }} />
                                            </div>
                                            <div className="input-block">
                                                <label className="input-label">Phone Number</label>
                                                <input 
    name="phone" 
    type="text" 
    className="form-control-premium" 
    value={profile.phone || ""} 
    onChange={handlePhoneChange} // Using a specific handler
    placeholder="1234567890"
    maxLength="10" 
    inputMode="numeric"
/>
                                            </div>
                                            <div className="input-block">
                                                <label className="input-label">Company Name</label>
                                                <input name="company_name" type="text" className="form-control-premium" value={profile.company_name || ""} onChange={handleProfileChange} />
                                            </div>
                                        </div>

                                        <div className="card-footer-actions">
                                            <button className="btn-save-changes" onClick={updateProfile}>Save Changes</button>
                                            <button className="btn-cancel">Cancel</button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* RIGHT: Password & Danger Zone */}
                        <div className="col-lg-6">
                            <div className="premium-card mb-4">
                                {isLoading ? (
                                    <SkeletonForm />
                                ) : (
                                    <>
                                        <div className="section-header">
                                            <div className="icon-wrap security-icon">
                                                <i className="fa-solid fa-lock"></i>
                                            </div>
                                            <div>
                                                <h5 className="card-title">Password & Security</h5>
                                                <p className="card-subtitle">Change your password</p>
                                            </div>
                                        </div>

                                        <div className="form-body">
                                            <div className="input-block position-relative">
                                                <label className="input-label">Current Password</label>
                                                <input
                                                    name="currentPassword"
                                                    type={showPassword.current ? "text" : "password"}
                                                    className="form-control-premium"
                                                    value={passwords.currentPassword}
                                                    onChange={handlePasswordChange}
                                                    placeholder="Enter current password"
                                                />
                                                <i
                                                    className={`fa-solid ${showPassword.current ? 'fa-eye-slash' : 'fa-eye'} position-absolute`}
                                                    style={{ right: '15px', top: '42px', cursor: 'pointer', color: '#6c757d' }}
                                                    onClick={() => toggleVisibility('current')}
                                                ></i>
                                            </div>

                                            <div className="input-block position-relative">
                                                <label className="input-label">New Password</label>
                                                <input
                                                    name="newPassword"
                                                    type={showPassword.new ? "text" : "password"}
                                                    className="form-control-premium"
                                                    value={passwords.newPassword}
                                                    onChange={handlePasswordChange}
                                                    placeholder="Enter new password"
                                                />
                                                <i
                                                    className={`fa-solid ${showPassword.new ? 'fa-eye-slash' : 'fa-eye'} position-absolute`}
                                                    style={{ right: '15px', top: '42px', cursor: 'pointer', color: '#6c757d' }}
                                                    onClick={() => toggleVisibility('new')}
                                                ></i>
                                            </div>

                                            <div className="input-block position-relative">
                                                <label className="input-label">Confirm New Password</label>
                                                <input
                                                    name="confirmPassword"
                                                    type={showPassword.confirm ? "text" : "password"}
                                                    className="form-control-premium"
                                                    value={passwords.confirmPassword}
                                                    onChange={handlePasswordChange}
                                                    placeholder="Confirm new password"
                                                />
                                                <i
                                                    className={`fa-solid ${showPassword.confirm ? 'fa-eye-slash' : 'fa-eye'} position-absolute`}
                                                    style={{ right: '15px', top: '42px', cursor: 'pointer', color: '#6c757d' }}
                                                    onClick={() => toggleVisibility('confirm')}
                                                ></i>
                                            </div>

                                            <button className="btn-update-password" onClick={updatePassword}>Update Password</button>
                                        </div>

                                        {/* <div className="danger-card mt-3">
                                            <div className="d-flex align-items-start gap-3">
                                                <i className="fa-solid fa-triangle-exclamation danger-icon"></i>
                                                <div className="flex-grow-1">
                                                    <h6 className="danger-title">Danger Zone</h6>
                                                    <p className="danger-text">Permanently delete your account and all data</p>
                                                    <button className="btn-delete-account">Delete Account</button>
                                                </div>
                                            </div>
                                        </div> */}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Simple CSS for Skeleton Animation */}
            <style>{`
                @keyframes shimmer {
                    0% { opacity: 0.5; }
                    50% { opacity: 1; }
                    100% { opacity: 0.5; }
                }
                .skeleton-line, .skeleton-circle {
                    animation: shimmer 1.5s infinite ease-in-out;
                }
            `}</style>
        </div>
    );
};

export default ProfileInfo;